import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react';

import {Button, Flex, Text, ToggleSwitch} from '@priority-software/priority-style-react';
import {
    getIsDisablePath,
    onBpmAction,
    toggleBpmFlow
} from '../../../../../../_shared/GWTBridge/invoke/bpm/bpmMain';
import {BpmActions} from '../../../../../../_shared/GWTBridge/invoke/bpm/types';
import {texts} from '../../texts';
import {CommonHelpbook} from '../../../../../../components/Common/CommonHelpbook';
import {BPMEvent, BPMEventTypes} from '../../types';
import {EntityNotification} from '../../../../../../features/Form/components/EntityNotification/EntityNotification';
import {NotificationType, bpmMsgNotificationStore} from '../../stores/BPMMsgNotification';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';

export const BPMHeaderToolbar = observer(() => {
    /**
     * `isActive` represents the state of the BPM flow and corresponds to the `disablePath` flag in GWT (in the edit menu).
     * Since `isActive` has the opposite meaning of the `disablePath` flag, its value will be the inverse of the GWT flag.
     *  - When the BPM flow is active, `getIsDisablePath()` will return `false`, making `isActive` true.
     *  - Conversely, when the BPM flow is inactive, `getIsDisablePath()` will return `true`, making `isActive` false.
     */
    const [isActive, setActive] = useState(!getIsDisablePath());
    const [BPMSaveDisabled, setBPMSaveDisabled] = useState(true);

    const {
        hideMsg,
        showMsg,
        params: {show: showBpmNotification, type, msg: notificationText}
    } = bpmMsgNotificationStore;
    const toggleBPMActive = () => {
        toggleBpmFlow(isActive);
    };

    const onBpmFlowSave = async () => {
        await onBpmAction(BpmActions.save);
    };

    useEffect(() => {
        const handleBPMEvent: (e: any) => void = async (e: BPMEvent) => {
            const detail = e.detail;

            if (detail) {
                const {data} = detail;

                if (data) {
                    if (data.type === BPMEventTypes.mainActive) {
                        setActive(data.action !== 'true');
                    } else if (data.type === BPMEventTypes.bpmChartSaved) {
                        showMsg(texts.save_changes_msg, NotificationType.success);
                    } else if (data.type === BPMEventTypes.bpmSaveDisabled) {
                        setBPMSaveDisabled('true' === data.action);
                    }
                }
            }
        };

        window.addEventListener('BpmChanges', handleBPMEvent);

        return () => {
            window.removeEventListener('BpmChanges', handleBPMEvent);
        };
    }, []);

    return (
        <Flex justifyContent="flex-end" style={{gap: 8}}>
            {showBpmNotification ? (
                <EntityNotification type={type} text={notificationText} onClose={hideMsg} />
            ) : (
                <Button
                    skin="primary"
                    size="small"
                    onClick={onBpmFlowSave}
                    disabled={BPMSaveDisabled}
                >
                    {texts.save_label}
                </Button>
            )}

            <Text tag="span" onClick={toggleBPMActive} skin="paragraph-2">
                {texts.toolbar_active_label}
            </Text>

            <ModernTooltip
                tooltip={texts.toolbar_active_tooltip}
                position="top-end"
                tooltipOffset={6}
                zIndex={999999}
            >
                <ToggleSwitch onChange={toggleBPMActive} checked={isActive} />
            </ModernTooltip>

            <CommonHelpbook
                helpTitle={texts.BPM_title}
                helpText={texts.BPM_help_text}
                searchString="BPM"
                monitorText="BPM help - header help button"
                tooltipText={texts.help_title}
            />
        </Flex>
    );
});
